<script setup lang="ts">
import { computed, ref } from 'vue';
import axios from 'axios';
import BaseButton from '../ui/buttons/BaseButton.vue';
import { AtSign, CircleDashed, ClipboardCheckIcon, ClipboardCopyIcon, ForwardIcon, ShieldCheck, ShieldEllipsis } from 'lucide-vue-next';
import BaseInput from '../ui/inputs/BaseInput.vue';
import { copyToClipboard } from '@/utils';
import { useClaimStore, useNotificationStore, useTenantStore } from '@/store';
import adminAPI from '@/services/api';
import AlertModal from '../ui/modals/AlertModal.vue';
import { AtSymbolIcon } from '@heroicons/vue/24/outline';

const dkimHostname = computed(() => useTenantStore().tenant.tenantBranding?.postmarkappDomain?.DKIMPendingHost || useTenantStore().tenant.tenantBranding?.postmarkappDomain?.DKIMHost);
const dkimValue = computed(() => useTenantStore().tenant.tenantBranding?.postmarkappDomain?.DKIMPendingTextValue || useTenantStore().tenant.tenantBranding?.postmarkappDomain?.DKIMTextValue);
const dkimVerified = computed(() => useTenantStore().tenant.tenantBranding?.postmarkappDomain?.DKIMVerified);
const dkimVerificationLoading = ref(false);

const returnPathHostname = computed(() => useTenantStore().tenant.tenantBranding?.postmarkappDomain?.ReturnPathDomain);
const returnPathValue = computed(() => useTenantStore().tenant.tenantBranding?.postmarkappDomain?.ReturnPathDomainCNAMEValue);
const returnPathVerified = computed(() => useTenantStore().tenant.tenantBranding?.postmarkappDomain?.ReturnPathDomainVerified);
const returnPathVerificationLoading = ref(false);

const cnameHostname = computed(() => useTenantStore().tenant.tenantBranding?.subdomainUri);
const cnameValue = "signature.upclaim.eu";
const cnameVerified = computed(() => useTenantStore().tenant.tenantBranding?.activeCNAME);
const cnameVerificationLoading = ref(false);

async function verifyDKIM() {
  const waitNotif = useNotificationStore().emitNotification({ title: "Vérification en cours...", type: 'wait'});
  dkimVerificationLoading.value = true;
  try {
    const updatedTenant = await adminAPI.verifySubdomainDNS();
    useTenantStore().$patch({ tenant: updatedTenant });
    if (!dkimVerified.value) {
      useNotificationStore().emitNotification({
        title: "DKIM non vérifié",
        message: "Veuillez ré-essayer dans quelques minutes ou vérifier votre configuration",
        type: "warning"
      });
    }
    else useNotificationStore().emitNotification({ title: "DKIM vérifié !", type: "success" });
  } finally {
    waitNotif.duration = 0;
    dkimVerificationLoading.value = false; 
  }
};

async function verifyReturnPath() {
  const waitNotif = useNotificationStore().emitNotification({ title: "Vérification en cours...", type: 'wait'});
  returnPathVerificationLoading.value = true;
  try {
    const updatedTenant = await adminAPI.verifySubdomainDNS();
    useTenantStore().$patch({ tenant: updatedTenant });
    if (!returnPathVerified.value) {
      useNotificationStore().emitNotification({
        title: "Return-Path non vérifié",
        message: "Veuillez ré-essayer dans quelques minutes ou vérifier votre configuration",
        type: "warning"
      });
    }
    else useNotificationStore().emitNotification({ title: "Return-Path vérifié !", type: "success" });
  } finally {
    waitNotif.duration = 0;
    returnPathVerificationLoading.value = false; 
  }
};

async function verifyCNAME() {
  const waitNotif = useNotificationStore().emitNotification({ title: "Vérification en cours...", type: 'wait'});
  cnameVerificationLoading.value = true;
  try {
    const updatedTenant = await adminAPI.verifySubdomainDNS();
    useTenantStore().$patch({ tenant: updatedTenant });
    if (!cnameVerified.value) {
      useNotificationStore().emitNotification({
        title: "CNAME non vérifié",
        message: "Veuillez ré-essayer dans quelques minutes ou vérifier votre configuration",
        type: "warning"
      });
    }
    else useNotificationStore().emitNotification({ title: "CNAME vérifié !", type: "success" });
  } finally {
    waitNotif.duration = 0;
    cnameVerificationLoading.value = false; 
  }
};

async function sendInstructionsEmail() {
  const waitNotif = useNotificationStore().emitNotification({ title: "Envoie en cours...", type: 'wait'});
  modalEmail.value = false;
  try {
    await adminAPI.sendDomainConfigurationEmail({ email: email.value });
    useNotificationStore().emitNotification({ title: `Instructions envoyées avec succès à « ${email.value} » !`, type: "success" });
  } finally {
    waitNotif.duration = 0;
    returnPathVerificationLoading.value = false; 
  } 
}

const modalEmail = ref(false);
const email = ref("");
</script>
<template>
  <AlertModal
    :open="modalEmail"
    type="info"
    title="Envoyer les instructions par email"
    confirm-message="Envoyer"
    @close="modalEmail = false"
    @cancel="modalEmail = false"
    @confirm="sendInstructionsEmail"
  >
  <BaseInput
    label="Adresse email"
    description="Par exemple, pour envoyer au service informatique"
    :value="email"
    v-model="email"
    placeholder="Adresse email"
    size="md"
    :icon="AtSymbolIcon"
  />
  </AlertModal>

<div class="w-full pb-16">
  <div class="w-full mb-6">
    <BaseButton text="Envoyer les instructions par email" size="lg" :icon="ForwardIcon" class="mx-auto" @click="modalEmail = true" />
  </div>

  <div class="p-6 bg-white border rounded-lg shadow-md">
    <h1 class="mb-4 text-2xl font-bold">Paramètres DNS</h1>
    <p class="mb-6 text-base text-gray-700">
      Rendez-vous chez votre fournisseur DNS et ajoutez les enregistrements DNS DKIM et Return-Path pour
      vérifier votre domaine et assurer une livraison efficace des emails.
    </p>

    <div class="space-y-6">
      <!-- DKIM Record -->
      <div class="p-4 border rounded-lg">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-2">
            <span :class="[
              'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center',
              dkimVerified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            ]">
              <ShieldCheck v-if="dkimVerified" :size="20" class="mr-1" />
              {{ dkimVerified ? 'Vérifié' : 'Non validé' }}
            </span>
            <h2 class="inline-flex items-start text-xl font-semibold">
              DKIM (Type: <kbd class="px-2 mx-1 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500">TXT</kbd>)
            </h2>
          </div>
          <BaseButton text="Vérifier →" type="submit" @click="verifyDKIM" :loading="dkimVerificationLoading" v-if="!dkimVerified" />
        </div>
        <div class="space-y-4" v-if="!dkimVerified">
          <BaseInput
            label="Nom d'hôte"
            :disabled="true"
            v-model="dkimHostname"
            :value="dkimHostname"
            placeholder="DKIM Hostname"
            size="md"
            :icon="ClipboardCopyIcon"
          />
          <div>
            <p class="block text-sm font-medium leading-6 text-gray-900">Valeur :</p>
            <textarea v-model="dkimValue" readonly placeholder="DKIM Value" :rows="4" class="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600"></textarea>
          </div>
        </div>
        <p v-if="!dkimVerified" class="mt-4 text-red-500">
          Nous n'avons pas pu trouver votre enregistrement DKIM. Retournez chez votre fournisseur DNS et vérifiez que le nom d'hôte est correctement configuré. Si le nom d'hôte et la valeur semblent corrects, il se peut que les changements DNS prennent jusqu'à 48 heures pour se propager. Nous continuerons à vérifier automatiquement et vous informerons une fois que ce sera vérifié.
        </p>
      </div>

      <!-- Return-Path Record -->
      <div class="p-4 border rounded-lg">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-2">
            <span :class="[
              'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center',
              returnPathVerified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            ]">
              <ShieldCheck v-if="returnPathVerified" :size="20" class="mr-1" />
              <ShieldEllipsis v-else :size="20" class="mr-1" />
              {{ returnPathVerified ? 'Vérifié' : 'Inactif' }}
            </span>
            <h2 class="inline-flex items-start text-xl font-semibold">
              Return-Path (Type: <kbd class="px-2 mx-1 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500">CNAME</kbd>)
            </h2>
          </div>
          <BaseButton text="Vérifier →" type="submit" @click="verifyReturnPath" :loading="returnPathVerificationLoading" v-if="!returnPathVerified" />
        </div>
        <div class="space-y-4" v-if="!returnPathVerified">
          <BaseInput
            label="Nom d'hôte"
            :value="returnPathHostname"
            placeholder="Return-Path Hostname"
            size="md"
            :icon="ClipboardCopyIcon"
            :disabled="true"
          />
          <BaseInput
            label="Valeur"
            v-model="returnPathValue"
            :value="returnPathValue"
            placeholder="Return-Path Value"
            size="md"
            :icon="ClipboardCopyIcon"
            :disabled="true"
          />
        </div>
      </div>


      <!-- CNAME Record -->
      <div class="p-4 border rounded-lg">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-2">
            <span :class="[
              'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center',
              cnameVerified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            ]">
              <ShieldCheck v-if="cnameVerified" :size="20" class="mr-1" />
              <ShieldEllipsis v-else :size="20" class="mr-1" />
              {{ cnameVerified ? 'Vérifié' : 'Inactif' }}
            </span>
            <h2 class="inline-flex items-start text-xl font-semibold">
              Routing Alias (Type: <kbd class="px-2 mx-1 py-1.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg dark:bg-gray-600 dark:text-gray-100 dark:border-gray-500">CNAME</kbd>)
            </h2>
          </div>
          <BaseButton text="Vérifier →" type="submit" @click="verifyCNAME" :loading="cnameVerificationLoading" v-if="!cnameVerified" />
        </div>
        <div class="space-y-4" v-if="!cnameVerified">
          <BaseInput
            label="Nom d'hôte"
            :value="cnameHostname"
            placeholder="CNAME Host"
            size="md"
            :icon="ClipboardCopyIcon"
            :disabled="true"
          />
          <BaseInput
            label="Valeur"
            :value="cnameValue"
            placeholder="CNAME Value"
            size="md"
            :icon="ClipboardCopyIcon"
            :disabled="true"
          />
        </div>
      </div>
    </div>

    <p class="mt-6 text-gray-600">
      Qu'en est-il de SPF ? Nous n'exigeons plus d'enregistrements DNS SPF car ils sont automatiquement gérés pour vous.
      <a href="#" class="text-blue-500 hover:underline">En savoir plus →</a>
    </p>
  </div>

  <div class="mt-8">
    <button @click="verifyCNAME" class="bg-blue-700 rounded-md text-white text-lg py-4 no-underline text-center block w-full shadow-[inset_0_2px_rgba(255,255,255,0.15)] outline outline-2 outline-blue-800">
      Confirmer la configuration →
    </button>
  </div>
</div>
</template>
